import React, { useEffect, useState } from 'react';
import { useOrganization } from './OrganizationContext';
import { getBrandLogoSrc, getBrandFallbackSrc, isDocumentDark } from './brandLogoUtils';

/**
 * Theme-aware brand logo — white Coursolo in dark mode; org logo when set.
 * While branding is loading and no cache/context branding exists, reserve space
 * so Coursolo chrome does not flash on tenant/org sessions.
 */
export default function OrgBrandLogo({
    className = 'h-9 w-auto object-contain max-h-9',
    alt,
    branding: brandingProp,
    dark: darkProp,
    onClick,
    as = 'img',
}) {
    const { branding: ctxBranding, organization, loading } = useOrganization();
    const branding = brandingProp || ctxBranding;
    const [dark, setDark] = useState(() => (darkProp != null ? Boolean(darkProp) : isDocumentDark()));

    useEffect(() => {
        if (darkProp != null) {
            setDark(Boolean(darkProp));
            return undefined;
        }
        const sync = () => setDark(isDocumentDark());
        sync();
        const obs = new MutationObserver(sync);
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, [darkProp]);

    // Avoid Coursolo flash while org branding is still resolving
    if (loading && !branding && !brandingProp) {
        const placeholder = (
            <div className={`${className} rounded-md bg-slate-200/60 dark:bg-slate-700/50 animate-pulse`} aria-hidden />
        );
        if (as === 'button') {
            return (
                <button type="button" onClick={onClick} className="shrink-0 flex items-center rounded-lg" disabled aria-busy="true">
                    {placeholder}
                </button>
            );
        }
        return placeholder;
    }

    const src = getBrandLogoSrc(branding, { dark });
    const label = alt || branding?.companyName || organization?.name || 'Coursolo';

    const handleError = (e) => {
        const el = e.target;
        el.onerror = null;
        el.src = getBrandFallbackSrc({ dark });
    };

    if (as === 'button') {
        return (
            <button type="button" onClick={onClick} className="shrink-0 flex items-center rounded-lg hover:opacity-90 transition-opacity">
                <img src={src} alt={label} className={className} onError={handleError} />
            </button>
        );
    }

    return <img src={src} alt={label} className={className} onError={handleError} />;
}
